import { useCallback, useEffect, useState } from 'react';

import { useDialogStateManager, useLoginProtected, useSearchParams } from 'my-core/hooks';
// import { canViewFullOnlineCourse } from 'my-core/online-course-utils';
import {
  getShouldShowWeekliesFirst,
  isStudySessionListable,
  isStudySessionUserRegistered,
} from 'my-core/study-session-utils';

import StudySessionBundleCheckout from 'my-components/StudySessionBundle/StudySessionBundleCheckout';

import { GradeBoosterBundleCard, StudySessionCard, WeeklyBundleCard } from './GradeBoosterComponents';
import { getWeeklyBundleGroupings } from './student-course-page-utils';
import { StudentCoursePageSectionHeader } from './StudentCoursePageCommon';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

const EMPTY_ARR = [];
export default function StudentCoursePageGradeBoostersList({
  assessmentName,
  assessments,
  course,
  // courseSubscription,
  currentUser,
  // onlineCourses = EMPTY_ARR,
  studySessionBundles = EMPTY_ARR,
  studySessions = EMPTY_ARR,
  subscription,
  // userCourse,
}) {
  const { otherSessions, weeklyBundleGroupings } = getWeeklyBundleGroupings(
    studySessions.filter(isStudySessionListable),
  );

  const [purchasedWeeklyGroupings, availableWeeklyGroupings] = weeklyBundleGroupings.reduce(
    (mem, g) => mem[isStudySessionUserRegistered(g[0].current_user_study_session_user) ? 0 : 1].push(g) && mem,
    [[], []],
  );
  const [purchasedStudySessions, availableStudySessions] = otherSessions.reduce(
    (mem, ss) => mem[isStudySessionUserRegistered(ss.current_user_study_session_user) ? 0 : 1].push(ss) && mem,
    [[], []],
  );

  // const [purchasedMockExams, availableMockExams] = onlineCourses.reduce(
  //   (mem, oc) => (
  //     mem[canViewFullOnlineCourse(oc, oc.current_user_online_course_user, subscription, userCourse) ? 0 : 1].push(oc),
  //     mem
  //   ),
  //   [[], []],
  // );

  const [activeAssessmentId, setActiveAssessmentId] = useState(null);
  const studySessionsByAssessment = Object.groupBy(availableStudySessions, ss => ss.assessment_id);
  const assessmentsWithBoosters = assessments?.map(a => [a, studySessionsByAssessment[a.id]]).filter(r => r[1]);

  const weeklyTutorialAtTop = !!assessments && getShouldShowWeekliesFirst();

  const availableBundles = studySessionBundles.filter(
    b =>
      !b.current_user_study_session_bundle_user &&
      b.study_sessions.length &&
      !b.study_sessions.some(
        ss =>
          purchasedStudySessions.some(pss => pss.id === ss.id) ||
          purchasedWeeklyGroupings.some(pb => pb.some(pss => pss.id === ss.id)),
      ),
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutBundleIdParam = Number(searchParams.bndlId) || null;
  const loginProtected = useLoginProtected();
  const [bundleCheckoutOpen, checkoutBundleId, bundleCheckoutActions] = useDialogStateManager();
  // Driven off the search param rather than the click so a guest deep-linked straight into checkout
  // gets the login form too, and lands back on the bundle once they are through it.
  useEffect(() => {
    if (checkoutBundleIdParam) {
      loginProtected(() => bundleCheckoutActions.open(checkoutBundleIdParam), {
        actionText: 'Purchase Bundle',
        messagingProduct: 'live-prep',
        source: 'bundle_checkout_search_param',
        onClose: () => setSearchParams({ bndlId: undefined }, true),
      });
    } else {
      bundleCheckoutActions.close();
    }
  }, [bundleCheckoutActions, checkoutBundleIdParam, loginProtected, setSearchParams]);

  // Looked up against the unfiltered list on purpose: buying the bundle enrolls the student in its
  // sessions, which would drop it out of availableBundles and tear the dialog down mid-confirmation.
  const checkoutBundle =
    checkoutBundleId &&
    studySessionBundles.find(b => b.id === checkoutBundleId && !b.current_user_study_session_bundle_user);
  const openBundleCheckout = useCallback(bundleId => setSearchParams({ bndlId: bundleId }, true), [setSearchParams]);
  const closeBundleCheckout = useCallback(() => setSearchParams({ bndlId: undefined }, true), [setSearchParams]);

  if (!(studySessions.length /*|| onlineCourses.length */)) return null;
  const hasPurchasedBoosters = purchasedStudySessions.length || purchasedWeeklyGroupings.length; // || purchasedMockExams.length

  const ssCardProps = {
    subscription,
    currentUser,
    course,
  };

  return (
    <>
      {!!availableBundles.length && (
        <section>
          <StudentCoursePageSectionHeader title="Bundle & Save" />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, containerType: 'inline-size' }}>
            {availableBundles.map(bundle => (
              <GradeBoosterBundleCard
                key={bundle.id}
                course={course}
                currentUser={currentUser}
                onPurchase={openBundleCheckout}
                studySessionBundle={bundle}
                subscription={subscription}
              />
            ))}
          </Box>
        </section>
      )}
      {!!checkoutBundle && (
        <StudySessionBundleCheckout
          course={course}
          onClose={closeBundleCheckout}
          open={bundleCheckoutOpen}
          studySessionBundle={checkoutBundle}
        />
      )}
      {!!hasPurchasedBoosters && (
        <section>
          <StudentCoursePageSectionHeader title="Your Grade Boosters for this Course" />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, containerType: 'inline-size' }}>
            {purchasedStudySessions.map(ss => (
              <StudySessionCard key={ss.id} {...ssCardProps} studySession={ss} />
            ))}
            {purchasedWeeklyGroupings.map(weeklyBundle => (
              <WeeklyBundleCard key={weeklyBundle[0].id} {...ssCardProps} weeklyBundle={weeklyBundle} />
            ))}
          </Box>
        </section>
      )}
      {!!(availableStudySessions.length || availableWeeklyGroupings.length /* || availableMockExams.length */) && (
        <section>
          <StudentCoursePageSectionHeader
            title={`${hasPurchasedBoosters ? 'Other' : 'Available'} ${assessmentName ? `${assessmentName} ` : ''}Grade Boosters`}
          />
          {!!assessmentsWithBoosters?.length && (
            <Tabs
              onChange={(_, v) => setActiveAssessmentId(v)}
              sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
              value={activeAssessmentId}
              variant="scrollable"
            >
              <Tab label="All" value={null} />
              {assessmentsWithBoosters.map(([a]) => (
                <Tab key={a.id} label={a.name} value={a.id} />
              ))}
            </Tabs>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, containerType: 'inline-size' }}>
            {activeAssessmentId &&
              assessmentsWithBoosters
                .find(([{ id }]) => id === activeAssessmentId)[1]
                .map(ss => <StudySessionCard key={ss.id} {...ssCardProps} studySession={ss} />)}
            {weeklyTutorialAtTop &&
              availableWeeklyGroupings.map(weeklyBundle => (
                <WeeklyBundleCard key={weeklyBundle[0].id} {...ssCardProps} weeklyBundle={weeklyBundle} />
              ))}
            {!activeAssessmentId &&
              availableStudySessions.map(ss => <StudySessionCard key={ss.id} {...ssCardProps} studySession={ss} />)}
            {!weeklyTutorialAtTop &&
              availableWeeklyGroupings.map(weeklyBundle => (
                <WeeklyBundleCard key={weeklyBundle[0].id} {...ssCardProps} weeklyBundle={weeklyBundle} />
              ))}
          </Box>
        </section>
      )}
    </>
  );
}
